import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuthStore } from '../../stores/authStore';
import { trpc } from '../../lib/api';
import { isAuthError } from './authGuardUtils';

interface AuthGuardProps {
  children: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const location = useLocation();
  const { isAuthenticated, clearAuth } = useAuthStore();
  const [isSharedUser, setIsSharedUser] = useState<boolean | null>(null);

  const { isLoading: authLoading, error: authError } = trpc.auth.me.useQuery(undefined, {
    enabled: isAuthenticated,
    retry: false,
  });

  const { data: profile, isLoading: profileLoading, error: profileError } = trpc.user.getProfile.useQuery(undefined, {
    enabled: isAuthenticated,
    retry: false,
  });

  useEffect(() => {
    if (authError && isAuthError(authError)) {
      // Token might be expired or invalid
      clearAuth();
    }
  }, [authError, clearAuth]);

  useEffect(() => {
    if (profile) {
      const userType =
        typeof profile === 'object' && 'userType' in profile
          ? (profile as { userType?: string | null }).userType
          : undefined;
      setIsSharedUser(userType === 'shared');
      return;
    }
    if (profileError && isAuthError(profileError)) {
      clearAuth();
      return;
    }
    if (profileError) {
      // Avoid blocking the UI on transient errors.
      setIsSharedUser(false);
    }
  }, [profile, profileError, clearAuth]);

  if (
    authLoading ||
    profileLoading ||
    (isAuthenticated && isSharedUser === null)
  ) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login page but save the attempted location
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 共有ユーザーの場合はshared-appにリダイレクト
  if (isSharedUser && !location.pathname.startsWith('/shared-app')) {
    return <Navigate to="/shared-app" replace />;
  }

  // 通常ユーザーが/shared-appにアクセスした場合は/appにリダイレクト
  if (!isSharedUser && location.pathname.startsWith('/shared-app')) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return <>{children}</>;
}
